import { range } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";

import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import WorkHistorySidebar from "@apps/work-history/components/work-history-sidebar";
import { currentOrganizationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import {
  ToggleButtonGroup,
  ToggleButtonGroupTheme,
  ToggleButtonGroupType,
} from "@components/toggle-button-group/toggle-button-group";
import { removeCurrentYear } from "@helpers/helpers";

import ProfileContainer from "./profile-container";

const LAST_7_DAYS_TITLE = "Last 7 Days";
// For the first 2 days of the current week/month there is little or no data in
// the period yet, so fall back to a rolling last-7-days summary instead of an
// almost-empty week-to-date / month-to-date one.
const SHOW_LAST_7_DAYS_BEFORE_DAY = 2;

const ProfileWorkHistoryMonth = ({
  profileId,
  monthToSubtract,
  onCompletedFetchingHistory,
}: {
  profileId: number;
  monthToSubtract: number;
  onCompletedFetchingHistory?: () => void;
}) => {
  const monthStartDate = moment()
    .startOf("month")
    .subtract(monthToSubtract, "months");
  const monthEndDate = monthStartDate.clone().endOf("month");
  const showLast7Days =
    moment().diff(monthStartDate, "days") < SHOW_LAST_7_DAYS_BEFORE_DAY;

  return (
    <ProfileContainer
      className="bg-white"
      title={
        showLast7Days ? LAST_7_DAYS_TITLE : monthStartDate.format("MMMM YYYY")
      }
    >
      <div className="-mt-4">
        <WorkHistorySidebar
          hideSummaryHeader
          userId={profileId}
          startDate={
            showLast7Days
              ? moment().subtract(7, "days").format("YYYY-MM-DD")
              : monthStartDate.format("YYYY-MM-DD")
          }
          endDate={
            showLast7Days || moment().isBefore(monthEndDate, "days")
              ? moment().format("YYYY-MM-DD")
              : monthEndDate.format("YYYY-MM-DD")
          }
          sectionClassName="px-0"
          topCollaboratorAvatarSize={5}
          onCompletedFetchingHistory={onCompletedFetchingHistory}
          forceDateFilter={true}
        />
      </div>
    </ProfileContainer>
  );
};

const ProfileWorkHistoryWeek = ({
  profileId,
  weekToSubtract,
  onCompletedFetchingHistory,
}: {
  profileId: number;
  weekToSubtract: number;
  onCompletedFetchingHistory?: () => void;
}) => {
  const weekStartDate = moment()
    .startOf("isoWeek")
    .subtract(weekToSubtract, "weeks");
  const weekEndDate = weekStartDate.clone().endOf("isoWeek");
  const showLast7Days =
    moment().diff(weekStartDate, "days") < SHOW_LAST_7_DAYS_BEFORE_DAY;

  return (
    <ProfileContainer
      className="bg-white"
      title={
        showLast7Days
          ? LAST_7_DAYS_TITLE
          : `${removeCurrentYear(
              weekStartDate.format("MMMM D, YYYY"),
            )} - ${removeCurrentYear(weekEndDate.format("MMMM D, YYYY"))}`
      }
    >
      <div className="-mt-4">
        <WorkHistorySidebar
          hideSummaryHeader
          userId={profileId}
          startDate={
            showLast7Days
              ? moment().subtract(7, "days").format("YYYY-MM-DD")
              : weekStartDate.format("YYYY-MM-DD")
          }
          endDate={
            showLast7Days || moment().isBefore(weekEndDate, "days")
              ? moment().format("YYYY-MM-DD")
              : weekEndDate.format("YYYY-MM-DD")
          }
          sectionClassName="px-0"
          topCollaboratorAvatarSize={5}
          onCompletedFetchingHistory={onCompletedFetchingHistory}
        />
      </div>
    </ProfileContainer>
  );
};

const ProfileWorkHistory = ({ profileId }: { profileId: number }) => {
  const defaultPage = 2;
  const organization = currentOrganizationVar();
  const [page, setPage] = useState(defaultPage);
  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();
  const [isFetchingHistory, setIsFetchingHistory] = useState(true);

  const handleCompletedFetchingHistory = () => {
    setIsFetchingHistory(false);
  };

  const handleShowMore = () => {
    setIsFetchingHistory(true);
    setPage(page + 1);
  };

  useEffect(() => {
    setPage(defaultPage);
  }, [uiPreferenceCache.profileWorkHistoryRange]);

  if (!organization.featureFlags.workHistory) {
    return null;
  }

  return (
    <div>
      <div className="flex items-center justify-between pt-2 mb-6">
        <div className="text-lg font-semibold text-gray-900">Work History</div>
        <ToggleButtonGroup<"week" | "month">
          theme={ToggleButtonGroupTheme.mergedLightBlueButtons}
          buttons={[
            {
              value: "week",
              title: "Week",
              active: uiPreferenceCache.profileWorkHistoryRange === "week",
            },
            {
              value: "month",
              title: "Month",
              active: uiPreferenceCache.profileWorkHistoryRange === "month",
            },
          ]}
          onClick={(option: ToggleButtonGroupType<"week" | "month">) => {
            saveUiPreference({ profileWorkHistoryRange: option.value });
          }}
        />
      </div>
      <div className="flex flex-col gap-6">
        {range(page).map((pageToSubtract) =>
          uiPreferenceCache.profileWorkHistoryRange === "week" ? (
            <ProfileWorkHistoryWeek
              key={pageToSubtract}
              profileId={profileId}
              weekToSubtract={pageToSubtract}
              onCompletedFetchingHistory={handleCompletedFetchingHistory}
            />
          ) : (
            <ProfileWorkHistoryMonth
              key={pageToSubtract}
              profileId={profileId}
              monthToSubtract={pageToSubtract}
              onCompletedFetchingHistory={handleCompletedFetchingHistory}
            />
          ),
        )}
        <div className="flex justify-center">
          <Button
            theme={buttonTheme.default}
            onClick={handleShowMore}
            text="Show more"
            disabled={isFetchingHistory}
          />
        </div>
      </div>
    </div>
  );
};

export default ProfileWorkHistory;
